import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { usePageMeta } from "@/lib/meta";
import Reveal, { Overline } from "@/components/Reveal";
import CtaBand from "@/components/home/CtaBand";

const SECTIONS = [
  {
    n: "01",
    title: "Leading Your Team",
    body: "Your audience reads your tone before they process your words. Whether it's an all-hands, a tough town hall Q&A, or a one-on-one with a direct report, I help you sound as clear and decisive as your strategy deserves.",
  },
  {
    n: "02",
    title: "Facing the Boardroom and the Public",
    body: "Directors, analysts, and journalists are listening for conviction as much as content. I help you present with authority, stay composed under pressure, and own the room, whether it's an earnings call, a keynote, or a live interview.",
  },
  {
    n: "03",
    title: "Deals and Negotiations",
    body: "When you're pitching investors, negotiating a merger, or repairing a major client relationship, how you say it can decide the outcome. I help you project confidence, hold your position, and be persuasive without pushing.",
  },
  {
    n: "04",
    title: "Crisis and Difficult Moments",
    body: "Layoffs, restructuring, and public setbacks test a leader's credibility in minutes. I help you deliver hard news with steadiness and humanity, so you protect trust when it's most at risk.",
  },
];

export default function Services() {
  usePageMeta(
    "Services — Executive Voice Coaching for Town Halls, Boardrooms & Media | Credible Presence",
    "Executive voice coaching for town halls, boardrooms, media, and the moments that matter — from an Austin executive voice consultant. Investor pitch coaching, founder pitch preparation, and leadership communication coaching, virtual or in person."
  );

  return (
    <div data-testid="services-page">
      <section className="relative overflow-hidden bg-[#F7F5F1] pt-40 pb-20 sm:pt-48 sm:pb-28">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <Reveal>
            <Overline>Services</Overline>
            <h1 className="mt-6 max-w-3xl font-serif text-4xl leading-[1.15] tracking-tight text-[#26282E] sm:text-5xl lg:text-6xl">
              The right voice for <em className="text-[#2F5D8C]">every room.</em>
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-[#5B5E66] sm:text-lg">
              Executive voice coaching for town halls, boardrooms, media, and the moments that matter.
            </p>
          </Reveal>
        </div>
      </section>

      <section>
        {SECTIONS.map((s, i) => (
          <article
            key={s.n}
            data-testid={`service-detail-${s.n}`}
            className={`py-20 sm:py-24 ${i % 2 === 1 ? "bg-[#E3D9C8]" : "bg-[#F7F5F1]"}`}
          >
            <div className="mx-auto grid max-w-7xl gap-10 px-6 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:px-12">
              <Reveal className="lg:col-span-5">
                <span className="font-mono text-[11px] tracking-[0.25em] text-[#2F5D8C]">{s.n}</span>
                <h2 className="mt-4 font-serif text-3xl leading-[1.2] tracking-tight text-[#26282E] sm:text-4xl">
                  {s.title}
                </h2>
              </Reveal>
              <div className="lg:col-span-7">
                <Reveal delay={0.1}>
                  <p className="max-w-xl text-base leading-relaxed text-[#5B5E66] sm:text-lg">{s.body}</p>
                  <Link
                    to="/contact"
                    data-testid={`service-cta-${s.n}`}
                    className="group mt-8 inline-flex items-center gap-3 rounded-md border border-[#2F5D8C]/50 px-5 py-3 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-[#2F5D8C] transition-colors duration-300 hover:bg-[#2F5D8C] hover:text-white"
                  >
                    Request a Confidential Consultation
                    <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </Reveal>
              </div>
            </div>
          </article>
        ))}
      </section>

      <CtaBand />
    </div>
  );
}
